import React, { FC, useState } from "react";

import { Download } from "lucide-react";

import { ISelectOption, Select } from "../../../shared/Selects";

type TMetric = 'vigilance' | 'bandwidth' | 'compliance' | 'bias' | 'impulsivity';
type TDepartment = 'all' | 'engineering' | 'sales' | 'hr' | 'finance' | 'executive';
interface TMatrixItem {
    title: string;
    shortTitle: string;
    color: 'primary' | 'success' | 'error' | 'warning' | 'info';
};
type TMatrices = Record<TMetric, TMatrixItem>;
type TScores = Record<TMetric, number>;
type TAnalyses = Record<TDepartment, TScores>;
export const CognitiveAssessmentOverview = () => {

    const teams: ISelectOption[] = [
        { value: "all", label: "All Teams" },
        { value: "engineering", label: "Engineering" },
        { value: "sales", label: "Sales" },
        { value: "hr", label: "HR" },
        { value: "finance", label: "Finance" },
        { value: "executive", label: "Executive" },
    ];

    const matrices: TMatrices = {
        vigilance: { title: 'Vigilance', shortTitle: 'Vigilance', color: 'error' },
        bandwidth: { title: 'Bandwidth', shortTitle: 'Bandwidth', color: 'primary' },
        compliance: { title: 'Compliance', shortTitle: 'Compliance', color: 'success' },
        bias: { title: 'Bias', shortTitle: 'Bias', color: 'warning' },
        impulsivity: { title: 'Impulsivity', shortTitle: 'Impulsivity', color: 'info' },
    };

    const analyses: TAnalyses = {
        all: { vigilance: 72, bandwidth: 68, compliance: 45, bias: 83, impulsivity: 56 },
        engineering: { vigilance: 64, bandwidth: 52, compliance: 71, bias: 76, impulsivity: 69 },
        sales: { vigilance: 78, bandwidth: 85, compliance: 62, bias: 58, impulsivity: 61 },
        hr: { vigilance: 69, bandwidth: 81, compliance: 55, bias: 74, impulsivity: 63 },
        finance: { vigilance: 57, bandwidth: 49, compliance: 82, bias: 66, impulsivity: 72 },
        executive: { vigilance: 83, bandwidth: 74, compliance: 68, bias: 71, impulsivity: 59 },
    };

    const [selectedTeam, setSelectedTeam] = useState<ISelectOption>(teams[0]);

    return (
        <div className='card card--md card--radius-md card--hover-shadow w-full dashboard__mcsa__cognitive_assessment_overview'>
            <div className='d-flex justify-content-between align-items-center mb-xl'>
                <div>
                    <p className='text-lg text-semibold'>Cognitive Assessment Overview</p>
                    <p className='text-sm color--txt-muted'>Multi-dimensional cognitive assessment</p>
                </div>
                <div className='d-flex align-items-center gap-sm'>
                    <Select
                        size='sm'
                        selected={selectedTeam}
                        options={teams}
                        onSelect={(option) => setSelectedTeam(option)}
                    />
                    <button className='btn btn--icon-md btn--radius-sm btn--secondary'>
                        <Download />
                    </button>
                </div>
            </div>
            <RadarChart data={analyses[selectedTeam.value as TDepartment]} />
            <div className='mt-xl'>
                {(Object.keys(matrices) as TMetric[]).map((item) => {
                    const teamKey = selectedTeam.value as TDepartment;
                    const analysis = analyses[teamKey];
                    return (
                        <div key={item} className='d-flex justify-content-between align-items-center mt-sm'>
                            <div className='d-flex align-items-center gap-sm'>
                                <div className={`w-md h-md br-full bg--${matrices[item].color}`} />
                                <p className='text-sm text-medium'>{matrices[item].title}</p>
                            </div>
                            <p className='text-sm text-medium'>{analysis[item]}%</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const METRICS: { key: TMetric; label: string; color: string; }[] = [
    { key: 'vigilance', label: 'Vigilance', color: '#FF4C91' },
    { key: 'bandwidth', label: 'Bandwidth', color: '#B399FF' },
    { key: 'compliance', label: 'Compliance', color: '#51FFC1' },
    { key: 'bias', label: 'Bias', color: '#FFB347' },
    { key: 'impulsivity', label: 'Impulsivity', color: '#00B7C3' },
];
interface RadarChartProps {
    data: Record<TMetric, number>;
    size?: number;
};
const RadarChart: FC<RadarChartProps> = ({ data, size = 256 }) => {
    const center = size / 2;
    const radius = size / 2 - 30;
    const levels = 5;

    const angleStep = (Math.PI * 2) / METRICS.length;

    const getPoint = (value: number, index: number) => {
        const angle = index * angleStep - Math.PI / 2;
        const r = (value / 100) * radius;
        return {
            x: center + r * Math.cos(angle),
            y: center + r * Math.sin(angle),
        };
    };

    const polygonPoints = METRICS.map((m, i) => {
        const p = getPoint(data[m.key], i);
        return `${p.x},${p.y}`;
    }).join(' ');

    return (
        <div className="mx-auto" style={{ width: '100%', aspectRatio: 1, maxWidth: 'fit-content' }}>
            <svg width={size} height={size} style={{ overflow: 'visible' }}>
                {/* Grid */}
                {[...Array(levels)].map((_, level) => {
                    const r = ((level + 1) / levels) * radius;
                    return (
                        <circle
                            key={level}
                            cx={center}
                            cy={center}
                            r={r}
                            fill="none"
                            stroke="rgba(0,0,0,0.08)"
                        />
                    );
                })}

                {/* Axes */}
                {METRICS.map((_, i) => {
                    const angle = i * angleStep - Math.PI / 2;
                    return (
                        <line
                            key={i}
                            x1={center}
                            y1={center}
                            x2={center + radius * Math.cos(angle)}
                            y2={center + radius * Math.sin(angle)}
                            stroke="rgba(0,0,0,0.1)"
                        />
                    );
                })}

                {/* Polygon */}
                <polygon
                    points={polygonPoints}
                    fill="rgba(139,92,246,0.25)"
                    stroke="#8B5CF6"
                    strokeWidth={2}
                />

                {/* Points */}
                {METRICS.map((m, i) => {
                    const p = getPoint(data[m.key], i);
                    return (
                        <circle
                            key={m.key}
                            cx={p.x}
                            cy={p.y}
                            r={5}
                            fill={m.color}
                            stroke={m.color}
                            strokeWidth={2}
                        />
                    );
                })}

                {/* Labels */}
                {METRICS.map((m, i) => {
                    const angle = i * angleStep - Math.PI / 2;
                    const x = center + (radius + 16) * Math.cos(angle);
                    const y = center + (radius + 16) * Math.sin(angle);
                    return (
                        <text
                            key={m.key}
                            x={x}
                            y={y}
                            fill="rgba(0,0,0,0.7)"
                            fontSize="12"
                            textAnchor="middle"
                            dominantBaseline="middle"
                        >
                            {m.label}
                        </text>
                    );
                })}
            </svg>
        </div>
    );
};